/* mv6-content.js — the v6 PDP's CONTENT layer (pass 2).
 *
 * Deliberately separate from mv6-calm-rest.js: that file is the money path's
 * UI binder and must not be able to break because a carousel threw. Nothing
 * here reads or writes a price. If you find yourself needing one, it belongs
 * in mv6-purchase.js and reaches this file through mv6.state().
 *
 * What lives here:
 *   · the ten-image gallery (slides, dots, thumb strip, arrows, counter,
 *     swipe, the inline review video)
 *   · the Supplement Facts sheet (open/close, swipe-to-dismiss, scroll guard)
 *   · every accordion (formula, benefits, FAQ, footer, references)
 *   · the topic tabs, the video carousel, the UGC review rail
 *   · scroll reveals and the two count-ups
 *   · the reviews wall, fed by Judge.me's public widget endpoint
 *
 * Rules it obeys:
 *   · NEVER scrollIntoView — always window.scrollTo with the sticky header's
 *     measured height subtracted.
 *   · No <video> element exists unless it is the one playing. A card that
 *     keeps a src behind a poster can hold audio; that was the "it started
 *     playing by itself" report.
 *   · Reveals are additive: without this file the page is fully visible.
 */
(function () {
  'use strict';

  var TAG = document.currentScript;
  var ROOT = document.getElementById(TAG.getAttribute('data-root'));
  if (!ROOT) { console.error('[mv6-content] section root not found'); return; }

  var SHOP = TAG.getAttribute('data-shop') || '';
  var PRODUCT_ID = TAG.getAttribute('data-product-id') || '';
  var WANT_REVIEWS = TAG.getAttribute('data-reviews') === '1';
  var LIVE_STATS = TAG.getAttribute('data-live-stats') === '1';

  function $(s, r) { return (r || ROOT).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || ROOT).querySelectorAll(s)); }
  function show(el, on) { if (el) { if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', ''); } }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); }
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function isMobile() { return window.innerWidth < 900; }

  /* Every programmatic scroll lands under the sticky header. */
  function scrollToEl(el, extra) {
    if (!el) return;
    var nav = $('[data-mv6-nav]');
    var pad = (nav ? nav.getBoundingClientRect().height : 84) + (extra || 12);
    var y = el.getBoundingClientRect().top + (window.scrollY || 0) - pad;
    window.scrollTo({ top: Math.max(0, y), behavior: REDUCED ? 'auto' : 'smooth' });
  }

  /* ══ GALLERY ═══════════════════════════════════════════════════════════ */
  var slides = $$('[data-mv6-slide]');
  var dots = $$('[data-mv6-gal-dot]');
  var thumbs = $$('[data-mv6-gal-thumb]');
  var track = $('[data-mv6-gal-track]');
  var thumbStrip = $('[data-mv6-gal-thumbs]');
  var img = 0;

  var EASE_IN = 'opacity .26s cubic-bezier(.33,0,.2,1), transform .32s cubic-bezier(.33,0,.2,1)';
  /* The OUTGOING slide does not travel — it only fades, at offset 0, under
     the incoming one, and its transform reset is delayed until it is
     invisible. Two slides moving apart opened a gap at the frame edge and
     read as "a square image for a second". */
  var EASE_OUT = 'opacity .26s cubic-bezier(.33,0,.2,1), transform .01s linear .28s';

  function paintGallery() {
    slides.forEach(function (el, i) {
      var on = i === img;
      el.style.transition = on ? EASE_IN : EASE_OUT;
      el.style.opacity = on ? '1' : '0';
      el.style.zIndex = on ? '2' : '1';
      el.style.transform = on ? 'translate3d(0,0,0)' : (i < img ? 'translate3d(-6%,0,0)' : 'translate3d(6%,0,0)');
    });
    dots.forEach(function (el, i) {
      el.style.width = i === img ? '22px' : '7px';
      el.style.background = i === img ? '#4A3A6E' : '#D8CDE9';
    });
    thumbs.forEach(function (el, i) { el.style.borderColor = i === img ? '#4A3A6E' : 'transparent'; });
    var count = $('[data-mv6-gal-count]');
    if (count) count.textContent = (img + 1) + ' / ' + slides.length;
    /* Badges and the price puck belong to the product shot only. */
    var ov = img === 0 ? '1' : '0';
    var badges = $('[data-mv6-gal-badges]'), puck = $('[data-mv6-gal-puck]');
    if (badges) badges.style.opacity = ov;
    if (puck) puck.style.opacity = ov;
    thumbSync();
    hydrateNear();
  }

  /* The thumb strip is not the offsetParent, so centring is measured with
     getBoundingClientRect, never offsetLeft. */
  function thumbSync() {
    if (!thumbStrip || !thumbs[img]) return;
    if (thumbStrip.offsetParent === null) return;
    var s = thumbStrip.getBoundingClientRect(), t = thumbs[img].getBoundingClientRect();
    var to = thumbStrip.scrollLeft + (t.left - s.left) - (s.width - t.width) / 2;
    thumbStrip.scrollTo({ left: Math.max(0, to), behavior: REDUCED ? 'auto' : 'smooth' });
  }

  /* LCP guard, two tiers. Only slide 1 carries a real src in the markup:
     the other slides are stacked IN the viewport so loading="lazy" cannot
     defer them, and a display:none thumbnail still downloads.
       · NEAR — this slide, both loop-neighbours and their thumbs, the moment
         the LCP image is in (or on the first interaction, whichever comes
         first), so a swipe never waits on a fetch.
       · ALL  — after window.load, unchanged.
     `galLive` is what keeps tier 1 off the critical path: paintGallery() runs
     at boot and calls hydrateNear(), which stays inert until then. Remove the
     gate and 205 KB of invisible WebP goes back beside the LCP image (R-58). */
  var galReady = false, galLive = false;
  function hydrate(el) {
    var src = el && el.getAttribute('data-mv6-src');
    if (src && el.getAttribute('src') !== src) el.setAttribute('src', src);
  }
  function hydrateNear() {
    if (galReady || !galLive) return;
    var n = slides.length;
    [img, (img + 1) % n, (img - 1 + n) % n].forEach(function (i) {
      hydrate(slides[i]);
      if (thumbs[i]) hydrate(thumbs[i].querySelector('img'));
    });
  }
  function galGo() {
    if (galLive) return;
    galLive = true;
    hydrateNear();
  }
  function hydrateAll() {
    if (galReady) return;
    galReady = true;
    galLive = true;
    $$('[data-mv6-src]').forEach(hydrate);
  }

  function setImg(i) {
    var n = slides.length;
    if (!n) return;
    i = ((i % n) + n) % n;
    if (i === img) return;
    img = i;
    galGo();
    paintGallery();
  }

  if (slides.length) {
    paintGallery();
    var lcpImg = slides[0];
    if (!lcpImg || lcpImg.complete) galGo();
    else {
      lcpImg.addEventListener('load', galGo, { once: true });
      lcpImg.addEventListener('error', galGo, { once: true });
      setTimeout(galGo, 2000);   /* a stalled LCP must not strand the gallery */
    }
    if (document.readyState === 'complete') setTimeout(hydrateAll, 400);
    else window.addEventListener('load', function () { setTimeout(hydrateAll, 400); }, { once: true });
    setTimeout(hydrateAll, 3500);
  }

  /* Swipe. Vertical panning stays with the browser (touch-action:pan-y); we
     claim the pointer only once movement is unambiguously horizontal, and a
     tap must never change the image. */
  if (track) {
    var sx = null, sy = 0, swiping = false, pid = null, galSup = false, galSupT = 0;
    track.addEventListener('pointerdown', function (e) {
      if (e.button != null && e.button !== 0) return;
      sx = e.clientX; sy = e.clientY; swiping = false; pid = e.pointerId;
    });
    track.addEventListener('pointermove', function (e) {
      if (sx == null || swiping) return;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) < 14 && Math.abs(dy) < 14) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.6) { sx = null; return; }
      swiping = true;
      try { track.setPointerCapture(pid); } catch (err) {}
    });
    function swipeEnd(e) {
      var started = swiping && sx != null;
      var dx = started ? e.clientX - sx : 0;
      sx = null; swiping = false;
      if (!started) return;
      /* Any completed horizontal drag — even one too short to change slides —
         must not be read as a tap: browsers still deliver a click after
         pointerup, and since 23 Aug 2026 a tap on the track opens the
         lightbox (the video rail's suppression pattern). */
      galSup = true;
      clearTimeout(galSupT);
      galSupT = setTimeout(function () { galSup = false; }, 320);
      if (dx < -40) setImg(img + 1);
      else if (dx > 40) setImg(img - 1);
    }
    track.addEventListener('pointerup', swipeEnd);
    track.addEventListener('pointercancel', swipeEnd);
    /* Tap-to-open (23 Aug 2026, Clarity dead clicks on the gallery): users
       tap product images expecting zoom. The expand button inside the track
       bubbles its click here too, so keyboard activation shares this path. */
    track.addEventListener('click', function () {
      if (galSup) return;
      lbSet(true);
    });
  }

  /* ══ GALLERY LIGHTBOX (23 Aug 2026, dead-click round) ═════════════════
   * Full-screen viewer for the gallery slides. Contracts it keeps:
   *   · the gallery's `img` index stays the single source of truth — viewer
   *     nav drives setImg(), so closing leaves the gallery on the same slide;
   *   · closed = display:none + hidden (R-75: no parked fixed layer);
   *   · scroll while open is REFUSED with the menu's event-guard pattern
   *     (R-122 class — no layout writes, so nothing can desync sticky, the
   *     header pin, or another overlay's lock);
   *   · bfcache restore closes it (R-128);
   *   · NO ZOOM anywhere (user call, 23 Aug — the enlarged view IS the zoom;
   *     wheel- and click-zoom were built and removed the same day: wheel read
   *     as broken page scrolling, click-zoom made the scrim cursor lie);
   *   · mobile is a VERTICAL SCROLL FEED of all slides (Amazon-style, user
   *     call) — the feed scrolls natively (the window guards exempt it, its
   *     overscroll is contained per R-126), and the scroll handler RESOLVES
   *     the nearest slide every frame (R-118) to sync counter + gallery;
   *   · desktop is one centered slide + arrows; click on the scrim closes,
   *     click on the image is inert (a missed arrow press must not dismiss). */
  var lb = $('[data-mv6-lb]'), lbStage = $('[data-mv6-lb-stage]'), lbImg = $('[data-mv6-lb-img]'), lbCount = $('[data-mv6-lb-count]'), lbFeed = $('[data-mv6-lb-feed]');
  var lbOpen = false, lbCloseT = 0, lbBuilt = false;
  function lbStop(e) {
    if (e.touches && e.touches.length > 1) return;   /* system pinch-zoom stays available */
    if (lbFeed && lbFeed.contains(e.target)) return;   /* the feed scrolls natively */
    e.preventDefault();
  }
  var LB_KEYS = { ' ': 1, Spacebar: 1, PageUp: 1, PageDown: 1, Home: 1, End: 1, ArrowUp: 1, ArrowDown: 1 };
  function lbKeydown(e) {
    if (!lbOpen) return;
    if (e.key === 'Escape') { lbSet(false); return; }
    if (e.key === 'ArrowLeft') { e.preventDefault(); lbNav(-1); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); lbNav(1); return; }
    if (LB_KEYS[e.key] && !e.metaKey && !e.ctrlKey && !e.altKey) e.preventDefault();
  }
  function lbPaint() {
    if (!lbImg) return;
    var el = slides[img];
    if (el) {
      hydrate(el);
      lbImg.setAttribute('src', lbSrcOf(el));
      lbImg.setAttribute('alt', el.getAttribute('alt') || '');
    }
    if (lbCount) lbCount.textContent = (img + 1) + ' / ' + slides.length;
  }
  /* ⚠ Sources come from data-mv6-src, NEVER from currentSrc (R-137).
     `currentSrc` does not update synchronously when hydrate() swaps `src` —
     it keeps reporting the 1×1 placeholder GIF until the real image has
     actually LOADED. On a slow in-app browser the viewer copied that
     placeholder for every not-yet-loaded slide and froze it there: the
     23 Aug Clarity session with two real slides and eight blank ones. */
  function lbSrcOf(el) {
    return el.getAttribute('data-mv6-src') || el.currentSrc || el.getAttribute('src');
  }
  /* The mobile feed is built once, on first open, from the gallery's own
     slides — one <img> per slide, stacked; the browser owns the scrolling. */
  function lbFeedBuild() {
    if (lbBuilt || !lbFeed) return;
    lbBuilt = true;
    slides.forEach(function (s) {
      hydrate(s);
      var im = document.createElement('img');
      im.src = lbSrcOf(s);
      im.alt = s.getAttribute('alt') || '';
      im.decoding = 'async';
      im.setAttribute('draggable', 'false');
      im.style.cssText = 'display:block; width:calc(100% - 24px); margin:12px auto; border-radius:14px; box-shadow:0 24px 60px -30px rgba(0,0,0,.7)';
      lbFeed.appendChild(im);
    });
  }
  function lbNav(d) {
    galGo();
    setImg(img + d);
    lbPaint();
  }
  function lbSet(on) {
    if (!lb || !lbImg || !slides.length || on === lbOpen) return;
    lbOpen = on;
    var m = on ? 'addEventListener' : 'removeEventListener';
    window[m]('wheel', lbStop, { passive: false });
    window[m]('touchmove', lbStop, { passive: false });
    document[m]('keydown', lbKeydown, true);
    clearTimeout(lbCloseT);
    if (on) {
      hydrateAll();   /* every slide is one scroll away in the viewer */
      lbPaint();
      lb.removeAttribute('hidden');
      lb.style.display = 'block';
      if (lbFeed && getComputedStyle(lbFeed).display !== 'none') {
        lbFeedBuild();
        var it = lbFeed.children[img];
        if (it) lbFeed.scrollTop = Math.max(0, it.offsetTop - 12);   /* open AT the current slide */
      }
      if (REDUCED) lb.style.opacity = '1';
      else requestAnimationFrame(function () { lb.style.opacity = '1'; });
      var c = $('[data-mv6-lb-close]');
      if (c) try { c.focus({ preventScroll: true }); } catch (err) {}
    } else {
      lb.style.opacity = '0';
      lbCloseT = setTimeout(function () {
        lb.style.display = 'none';
        lb.setAttribute('hidden', '');
      }, REDUCED ? 0 : 280);
      var x = $('[data-mv6-gal-expand]');
      if (x) try { x.focus({ preventScroll: true }); } catch (err) {}
    }
  }
  if (lb && lbImg) {
    if (REDUCED) lb.style.transition = 'none';
    /* Desktop: click on the scrim closes; click on the image is inert so a
       missed arrow press never dismisses the viewer. No zoom (user call,
       23 Aug 2026) — the enlarged view IS the zoom, so no zoom cursor either. */
    if (lbStage) lbStage.addEventListener('click', function (e) {
      var r = lbImg.getBoundingClientRect();
      var inImg = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inImg) lbSet(false);
    });
    /* Mobile feed → counter + gallery sync. The handler RESOLVES the nearest
       slide every frame (R-118) — best is recomputed from scratch, never
       early-returned, so fast flings still land on the right index. */
    if (lbFeed) {
      var lbTick = false;
      lbFeed.addEventListener('scroll', function () {
        if (lbTick || !lbOpen) return;
        lbTick = true;
        requestAnimationFrame(function () {
          lbTick = false;
          var mid = lbFeed.scrollTop + lbFeed.clientHeight / 2, best = 0;
          for (var i = 0; i < lbFeed.children.length; i++) {
            if (lbFeed.children[i].offsetTop <= mid) best = i;
          }
          /* At the very bottom nothing below the mid-line can win — resolve to
             the LAST slide (R-121 class, same shape as the reveal's bottom fix)
             or the counter reads 9/10 with the 10th slide fully on screen. */
          if (lbFeed.scrollTop >= lbFeed.scrollHeight - lbFeed.clientHeight - 2) best = lbFeed.children.length - 1;
          if (best !== img) { galGo(); setImg(best); }
          if (lbCount) lbCount.textContent = (best + 1) + ' / ' + slides.length;
        });
      }, { passive: true });
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) lbSet(false); });   /* R-128 */
  }

  /* Mouse drag-scroll on the thumb strip. Touch returns early so the browser
     keeps its own momentum scrolling, and a >4px drag swallows the click so
     dragging never also selects a thumbnail. */
  if (thumbStrip) {
    var tDown = false, tX = 0, tLeft = 0, tMoved = 0;
    thumbStrip.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || (e.button != null && e.button !== 0)) return;
      tDown = true; tMoved = 0; tX = e.clientX; tLeft = thumbStrip.scrollLeft;
      thumbStrip.style.cursor = 'grabbing';
    });
    thumbStrip.addEventListener('pointermove', function (e) {
      if (!tDown) return;
      var d = e.clientX - tX;
      tMoved = Math.max(tMoved, Math.abs(d));
      thumbStrip.scrollLeft = tLeft - d;
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      thumbStrip.addEventListener(ev, function () { tDown = false; thumbStrip.style.cursor = 'grab'; });
    });
    thumbStrip.addEventListener('click', function (e) {
      if (tMoved > 4) { e.stopPropagation(); e.preventDefault(); tMoved = 0; }
    }, true);
  }

  /* Gallery review video — inline in its own thumbnail, no lightbox. The
     element is created on play and removed on end, so nothing can hold audio
     behind a poster. */
  var gv = $('[data-mv6-gv]');
  var gvEl = null, gvMuted = false;
  function gvStop() {
    if (gvEl) { try { gvEl.pause(); } catch (e) {} gvEl.remove(); gvEl = null; }
    var b = $('[data-mv6-gv-play]'), w = $('[data-mv6-gv-watch]');
    if (b) b.style.display = 'flex';
    if (w) w.style.display = 'block';
  }
  function gvPlay() {
    if (!gv || gvEl) return;
    gvEl = document.createElement('video');
    gvEl.src = 'https://cdn.shopify.com/videos/c/o/v/691c1f29544d4ca5a4bc9d280a0f9a81.mp4';
    gvEl.playsInline = true;
    gvEl.setAttribute('playsinline', '');
    gvEl.preload = 'none';
    gvEl.muted = gvMuted;
    gvEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; background:#000; z-index:2; cursor:pointer';
    gvEl.addEventListener('ended', gvStop);
    gvEl.addEventListener('click', function () { if (gvEl.paused) gvEl.play().catch(function () {}); else gvEl.pause(); });
    gv.appendChild(gvEl);
    var b = $('[data-mv6-gv-play]'), w = $('[data-mv6-gv-watch]');
    if (b) b.style.display = 'none';
    if (w) w.style.display = 'none';
    /* Sound on, with a muted retry when the browser blocks the first play(). */
    gvEl.play().catch(function () { gvEl.muted = true; gvMuted = true; gvEl.play().catch(gvStop); });
  }

  /* ══ SUPPLEMENT FACTS SHEET ════════════════════════════════════════════ */
  var fxWrap = $('[data-mv6-facts-wrap]');
  var fxCard = $('[data-mv6-facts-card]');
  var fxScrim = $('[data-mv6-facts-scrim]');
  var FX_EASE = 'transform .3s cubic-bezier(.33,0,.2,1), opacity .24s cubic-bezier(.33,0,.2,1)';
  var factsOpen = false;

  /* iOS ignores overflow:hidden on html/body often enough to be unreliable —
     it was reported as "sometimes I can scroll behind the sheet, sometimes I
     can't", and it depended on where the page was when the sheet opened. The
     only lock that holds there pins the body at its current offset and restores
     the scroll position on close (R-52). The class still carries the overflow
     rules; this adds the offset. Desktop never takes it: overflow:hidden on
     html re-resolves the sticky gallery column (R-26). */
  var fxScrollY = 0;
  function fxLock(on) {
    var html = document.documentElement, has = html.classList.contains('mv6-facts-locked');
    if (on === has) return;
    if (on) {
      fxScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = (-fxScrollY) + 'px';
      html.classList.add('mv6-facts-locked');
    } else {
      /* ROUND 19 — kept in the standard order (atomic within one task, so nothing
         paints at the intermediate scroll). The header flash on close was NOT
         here: it was the nav's own accumulator taking `lastY` from the pinned
         body's bogus scrollY of 0 and reading the restore as a 3000px
         scroll-down. Fixed in mv6-header.js's navSync. */
      html.classList.remove('mv6-facts-locked');
      document.body.style.top = '';
      window.scrollTo(0, fxScrollY);
    }
  }

  var fxOffTimer = 0;

  function setFacts(on) {
    if (!fxWrap) return;
    factsOpen = on;
    /* Round 19: the wrapper is display:none (`data-mv6-off`) while closed — a
       full-viewport fixed layer held at opacity:0 was one of the three overlays
       the user's ?mv6t=nooverlays A/B cleared the footer band with, and on iOS
       visibility:hidden did not release it. Un-drop a frame BEFORE the open
       transition; re-drop only after the close transition has finished. */
    clearTimeout(fxOffTimer);
    if (on) { fxWrap.removeAttribute('data-mv6-off'); void fxWrap.offsetWidth; }
    /* One attribute; the !important CSS behind it carries opacity, transform
       and pointer-events. Inline writes are cleared first so a drag left over
       from a previous gesture cannot outrank the rules — and, on the closing
       path, so the transition runs from wherever the finger left the card to
       the closed position. */
    fxCard.style.transition = FX_EASE;
    fxScrim.style.transition = 'opacity .24s cubic-bezier(.33,0,.2,1)';
    fxCard.style.removeProperty('transform');
    fxCard.style.removeProperty('opacity');
    fxScrim.style.removeProperty('opacity');
    if (on) fxWrap.setAttribute('data-mv6-facts-on', '');
    else {
      fxWrap.removeAttribute('data-mv6-facts-on');
      fxOffTimer = setTimeout(function () { if (!factsOpen) fxWrap.setAttribute('data-mv6-off', ''); }, 420);
    }
    /* Mobile ALSO takes a soft overflow lock: the event guard alone let iOS
       chain the gesture to the document once the sheet's own scroller hit an
       end. Safe at mobile width precisely because there is no sticky column
       there — on desktop `overflow:hidden` on html re-resolves the sticky
       gallery column and the left half of the page appears to vanish.
       ⚠ Its OWN class, not the drawer's `mv6-locked`: closing the sheet must
       never be able to unlock a page the cart drawer has locked. */
    fxLock(on && isMobile());
    if (on) fxCard.scrollTop = 0;
  }
  /* Desktop relies on this guard alone. Scrolling inside the card is let
     through; the card carries overscroll-behavior:contain. */
  function guard(e) {
    if (!factsOpen) return;
    if (fxCard && fxCard.contains(e.target)) return;
    e.preventDefault();
  }
  window.addEventListener('wheel', guard, { passive: false });
  window.addEventListener('touchmove', guard, { passive: false });

  /* Swipe down to dismiss (mobile). It only arms while the card's scroller is
     at the top and the finger moves DOWN, so a normal scroll inside the sheet
     is never hijacked.
     ⚠ Four things keep it smooth on a real phone, and all four must stay:
       · ONE requestAnimationFrame write per frame (touchmove only records)
       · transition:none and will-change set ONCE when the drag goes live
       · the scrim's transition killed for the duration — otherwise every
         per-frame opacity write is eased over 240ms and the scrim lags
       · touch-action:none on the card only while a drag is live, or iOS
         rubber-bands the scroller under our transform */
  if (fxCard) {
    var dY = 0, dStart = 0, dLive = false, dArmed = false, dT0 = 0, dRaf = 0;
    fxCard.addEventListener('touchstart', function (e) {
      if (!factsOpen || !isMobile() || e.touches.length !== 1) return;
      dArmed = fxCard.scrollTop <= 0;
      dStart = e.touches[0].clientY; dY = 0; dLive = false; dT0 = Date.now();
    }, { passive: true });
    fxCard.addEventListener('touchmove', function (e) {
      if (!dArmed) return;
      var d = e.touches[0].clientY - dStart;
      if (!dLive) {
        if (d < 8) return;
        dLive = true;
        fxCard.style.transition = 'none';
        fxCard.style.willChange = 'transform';
        fxCard.style.touchAction = 'none';
        fxScrim.style.transition = 'none';
      }
      dY = d > 150 ? 150 + (d - 150) * 0.35 : d;
      if (!dRaf) dRaf = requestAnimationFrame(function () {
        dRaf = 0;
        /* Inline !important: the open-state CSS is itself !important, and a
           live gesture has to outrank it. Removed again on release. */
        fxCard.style.setProperty('transform', 'translateY(' + dY + 'px)', 'important');
        fxScrim.style.setProperty('opacity', String(Math.max(0, 1 - dY / 320)), 'important');
      });
    }, { passive: true });
    function dragEnd() {
      if (!dLive) { dArmed = false; return; }
      dLive = false; dArmed = false;
      if (dRaf) { cancelAnimationFrame(dRaf); dRaf = 0; }
      fxCard.style.willChange = '';
      fxCard.style.touchAction = '';
      /* ⚠ RE-STATE the transition, never clear it: clearing an inline property
         does not restore a value nothing rewrites, and the sheet then closes
         instantly. */
      fxCard.style.transition = FX_EASE;
      fxScrim.style.transition = 'opacity .24s cubic-bezier(.33,0,.2,1)';
      var flick = dY > 44 && (Date.now() - dT0) < 280;
      if (dY > 92 || flick) {
        /* setFacts removes the drag's inline transform, so the close animates
           from wherever the finger left the card. (When this state lived in
           inline styles it had to KEEP the offset instead — clearing it threw
           the card back to 0 and animated out from there, a ~150px snap. With
           the CSS rules holding the closed value there is nothing to snap to.) */
        setFacts(false);
      } else {
        fxCard.style.removeProperty('transform');
        fxScrim.style.removeProperty('opacity');
      }
      dY = 0;
    }
    fxCard.addEventListener('touchend', dragEnd);
    fxCard.addEventListener('touchcancel', dragEnd);
  }

  /* ══ ACCORDIONS ════════════════════════════════════════════════════════ */
  /* Images inside a CLOSED accordion panel. A lazy img in a hidden panel never
     fetches, so it lands half a second AFTER the panel opens — that was the
     "the icon appears late" report on the formula cards and the benefit photos.
     Making them eager instead puts five large photos into the initial load,
     competing with the LCP image. So: they start lazy, and when their section
     comes within a screen and a half we un-lazy them.
     ⚠ Any image that is an ALTERNATE STATE of visible content belongs in this
     set by definition — an accordion photo swap, or the benefits "no row open"
     default photo, which only appears once a row is CLOSED again. The user is
     looking at the section when they trigger it, so there is no time left to
     load. That default photo was the third report of this same flash. */
  function warmIn(scope) {
    $$('img[data-mv6-eager]', scope).forEach(function (im) {
      im.removeAttribute('data-mv6-eager');
      /* Strip `loading` outright rather than warming a copy: an eager img
         fetches even while its panel is `hidden`, so the bytes are there
         before the panel can be opened. Warming a separate new Image() and
         leaving the img lazy was tried and races — the copy can still be in
         flight when the panel opens, which is the empty-block flash. */
      im.removeAttribute('loading');
    });
  }
  $$('img[data-mv6-eager]').forEach(function (im) {
    if (im.closest('[hidden]')) im.setAttribute('loading', 'lazy');
  });
  if (window.IntersectionObserver) {
    var warmed = [];
    $$('img[data-mv6-eager]').forEach(function (im) {
      var sec = im.closest('section');
      if (!sec || warmed.indexOf(sec) > -1) return;
      warmed.push(sec);
      var wo = new IntersectionObserver(function (es) {
        if (!es[0].isIntersecting) return;
        wo.disconnect();
        warmIn(sec);
      }, { rootMargin: '1400px 0px' });
      wo.observe(sec);
    });
  }

  function accSet(key, on, btn) {
    var panel = $('[data-mv6-acc-panel="' + key + '"]');
    if (!panel) return;
    if (on) warmIn(panel);
    show(panel, on);
    if (!btn) btn = $('[data-mv6-acc="' + key + '"]');
    if (!btn) return;
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    var plus = btn.querySelector('[data-mv6-acc-plus]');
    if (plus) plus.setAttribute('d', on ? 'M5 12h14' : 'M12 5v14M5 12h14');
    var chev = btn.querySelector('[data-mv6-acc-chev]');
    if (chev) chev.style.transform = on ? 'rotate(180deg)' : 'rotate(0deg)';
    /* Buy-box rows: the open row becomes a white card wrapping title + body.
       One attribute — the CSS carries the six properties, so it can never lose
       a specificity fight with the wrapper's inline border-bottom. */
    var wrap = $('[data-mv6-accwrap="' + key + '"]');
    if (wrap) {
      if (on) wrap.setAttribute('data-mv6-open', '');
      else wrap.removeAttribute('data-mv6-open');
    }
    /* The two nested toggles (the facts image, the testing standards) carry
       both of their labels in one attribute. */
    var lab = btn.getAttribute('data-mv6-acc-label');
    if (lab) {
      var parts = lab.split('|');
      var txt = Array.prototype.filter.call(btn.childNodes, function (n) {
        return n.nodeType === 3 && n.textContent.trim();
      })[0];
      if (txt) txt.textContent = on ? parts[1] : parts[0];
    }
  }

  /* The benefits rail is single-open: the open row also swaps the sticky
     photo beside it, and two photos cannot show at once. */
  function benPhoto(key) {
    $$('[data-mv6-ben-photo]').forEach(function (el) {
      show(el, el.getAttribute('data-mv6-ben-photo') === (key || 'default'));
    });
  }

  /* ══ TOPIC TABS ════════════════════════════════════════════════════════ */
  function setTopic(i) {
    $$('[data-mv6-topic]').forEach(function (b, k) {
      var on = k === i;
      b.style.background = on ? 'linear-gradient(120deg,#4A3A6E,#6E5E86)' : '#FFFFFF';
      b.style.color = on ? '#FFFFFF' : '#4A3A6E';
      b.style.borderColor = on ? '#4A3A6E' : '#DED2F2';
    });
    $$('[data-mv6-topic-panel]').forEach(function (p, k) { show(p, k === i); });
  }
  setTopic(0);

  /* ══ VIDEO CAROUSEL ════════════════════════════════════════════════════ */
  var vidRail = $('[data-mv6-vid-rail]');
  var vidRoot = $('[data-mv6-vid-root]');
  var vidSlots = vidRail ? $$('[data-mv6-vid-slot]', vidRail) : [];
  var VID_N = vidSlots.length / 2;
  var vidIdx = 0, vidEl = null, vidMuted = false;

  function vidStopAll() {
    if (vidEl) {
      try { vidEl.pause(); } catch (e) {}
      var slot = vidEl.parentNode;
      vidEl.remove();
      vidEl = null;
      if (slot) {
        var pb = slot.querySelector('[data-mv6-vid-play]');
        if (pb) pb.style.display = 'flex';
        var mb = slot.querySelector('[data-mv6-vid-mute]');
        if (mb) mb.remove();
      }
    }
  }

  function vidPlay(slot) {
    vidStopAll();
    var src = slot.getAttribute('data-mv6-vid-src');
    if (!src) return;
    vidEl = document.createElement('video');
    vidEl.src = src;
    vidEl.playsInline = true;
    vidEl.setAttribute('playsinline', '');
    vidEl.preload = 'none';
    vidEl.muted = vidMuted;
    vidEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; background:#000; z-index:2; cursor:pointer';
    vidEl.addEventListener('ended', vidStopAll);
    vidEl.addEventListener('click', function () { if (vidEl.paused) vidEl.play().catch(function () {}); else vidEl.pause(); });
    slot.appendChild(vidEl);

    var mute = document.createElement('button');
    mute.type = 'button';
    mute.setAttribute('aria-label', 'Toggle sound');
    mute.setAttribute('data-mv6-vid-mute', '');
    mute.style.cssText = 'position:absolute; top:12px; right:12px; z-index:3; width:38px; height:38px; border-radius:999px; border:none; cursor:pointer; background:rgba(20,14,30,.55); display:flex; align-items:center; justify-content:center';
    function paintMute() {
      mute.innerHTML = vidMuted
        ? '<svg width="19" height="19" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H3v6h3l5 4V5z" fill="#FFFFFF"></path><path d="M16 9l5 6M21 9l-5 6" stroke="#FFFFFF" stroke-width="1.8" stroke-linecap="round"></path></svg>'
        : '<svg width="19" height="19" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H3v6h3l5 4V5z" fill="#FFFFFF"></path><path d="M15.5 8.5a5 5 0 010 7M18 6a8.5 8.5 0 010 12" stroke="#FFFFFF" stroke-width="1.8" stroke-linecap="round"></path></svg>';
    }
    paintMute();
    mute.addEventListener('click', function (e) {
      e.stopPropagation();
      vidMuted = !vidMuted;
      if (vidEl) vidEl.muted = vidMuted;
      paintMute();
    });
    slot.appendChild(mute);

    var pb = slot.querySelector('[data-mv6-vid-play]');
    if (pb) pb.style.display = 'none';
    vidEl.play().catch(function () { vidEl.muted = true; vidMuted = true; paintMute(); vidEl.play().catch(vidStopAll); });
  }

  function vidPaint(snap) {
    if (!vidRail) return;
    var per = isMobile() ? 100 : 100 / 3;
    vidRail.style.transition = snap ? 'none' : 'transform .55s cubic-bezier(.22,.61,.36,1)';
    vidRail.style.transform = 'translateX(-' + (vidIdx * per) + '%)';
    $$('[data-mv6-vid-dot]').forEach(function (d, i) {
      var on = ((vidIdx % VID_N) + VID_N) % VID_N === i;
      d.style.width = on ? '26px' : '9px';
      d.style.background = on ? '#4A3A6E' : 'rgba(84,74,98,.28)';
    });
  }
  function vidStep(dir) {
    vidStopAll();
    vidIdx += dir;
    vidPaint(false);
    /* The rail duplicates the set, so a wrap can snap silently once the
       animation has landed. */
    setTimeout(function () {
      if (vidIdx >= VID_N) { vidIdx -= VID_N; vidPaint(true); }
      else if (vidIdx < 0) { vidIdx += VID_N; vidPaint(true); }
    }, 560);
  }
  if (vidRail) vidPaint(true);

  if (vidRoot && window.IntersectionObserver) {
    new IntersectionObserver(function (es) { if (!es[0].isIntersecting) vidStopAll(); }, { threshold: 0 }).observe(vidRoot);
  }

  if (vidRoot) {
    var vx = null, vy = 0, vw = false, vSup = false, vSupT = 0;
    var win = $('[data-mv6-vid-window]');
    if (win) {
      win.addEventListener('pointerdown', function (e) {
        if (e.button != null && e.button !== 0) return;
        vx = e.clientX; vy = e.clientY; vw = false;
      });
      win.addEventListener('pointermove', function (e) {
        if (vx == null || vw) return;
        var dx = e.clientX - vx, dy = e.clientY - vy;
        if (Math.abs(dx) < 14 && Math.abs(dy) < 14) return;
        if (Math.abs(dx) < Math.abs(dy) * 1.6) { vx = null; return; }
        vw = true;
        try { win.setPointerCapture(e.pointerId); } catch (err) {}
      });
      function vEnd(e) {
        var ok = vw && vx != null;
        var dx = ok ? e.clientX - vx : 0;
        vx = null; vw = false;
        if (!ok) return;
        /* Swallow the click a swipe would otherwise deliver to the card. */
        vSup = true;
        clearTimeout(vSupT);
        vSupT = setTimeout(function () { vSup = false; }, 300);
        if (dx < -40) vidStep(1);
        else if (dx > 40) vidStep(-1);
      }
      win.addEventListener('pointerup', vEnd);
      win.addEventListener('pointercancel', vEnd);
      win.addEventListener('click', function (e) { if (vSup) { e.stopPropagation(); e.preventDefault(); } }, true);
    }
  }

  /* ══ UGC REVIEW RAIL ═══════════════════════════════════════════════════
   * The rail is `scroll-snap-type: x mandatory` with `scroll-snap-align:
   * center`. Never advance it by a COMPUTED DISTANCE: one card width plus a
   * gap overshoots the next snap point's midpoint, so the browser settles on
   * the one after it and a single arrow click moves two cards. Always name a
   * target index and centre it — then the snap agrees with us, and card N-1
   * is left half-visible on the way in, which is the intended look. */
  var ugRail = $('[data-mv6-ug-rail]');
  function ugCards() { return ugRail ? Array.prototype.slice.call(ugRail.children) : []; }
  /* PITCH, measured: the distance between two card left edges. Everything else
     derives from it, so the gap token can change without touching this. */
  function ugPitch() {
    var c = ugCards();
    if (c.length < 2) return 0;
    return c[1].getBoundingClientRect().left - c[0].getBoundingClientRect().left;
  }
  /* The scrollLeft that CENTRES card i, clamped to the scrollable range. A
     374px card in an 850px rail cannot actually be centred at either end, so
     the ends clamp — which is why nearest-centre alone was wrong (it reported
     card 1 as active at scrollLeft 0, and the dots followed it). */
  function ugTarget(i) {
    var c = ugCards();
    if (!c.length) return 0;
    var w = c[0].getBoundingClientRect().width;
    var max = ugRail.scrollWidth - ugRail.clientWidth;
    return Math.max(0, Math.min(max, i * ugPitch() - (ugRail.clientWidth - w) / 2));
  }
  function ugIndex() {
    var c = ugCards();
    if (!c.length) return 0;
    var max = ugRail.scrollWidth - ugRail.clientWidth;
    var x = ugRail.scrollLeft;
    if (x <= 8) return 0;
    if (x >= max - 8) return c.length - 1;
    var p = ugPitch() || 1;
    var off = (ugRail.clientWidth - c[0].getBoundingClientRect().width) / 2;
    return Math.max(0, Math.min(c.length - 1, Math.round((x + off) / p)));
  }
  function ugGo(i) {
    var c = ugCards();
    if (!c.length) return;
    i = Math.max(0, Math.min(c.length - 1, i));
    ugRail.scrollTo({ left: ugTarget(i), behavior: REDUCED ? 'auto' : 'smooth' });
  }
  function ugSync() {
    if (!ugRail) return;
    var cards = ugCards();
    if (!cards.length) return;
    var max = ugRail.scrollWidth - ugRail.clientWidth;
    var x = ugRail.scrollLeft;
    var i = ugIndex();
    $$('[data-mv6-ug-dot]').forEach(function (d, k) {
      d.style.width = k === i ? '22px' : '7px';
      d.style.background = k === i ? '#4A3A6E' : '#D8CCEB';
    });
    var l = $('[data-mv6-ug-arrow="-1"]'), r = $('[data-mv6-ug-arrow="1"]');
    var fl = $('[data-mv6-ug-fade="l"]'), fr = $('[data-mv6-ug-fade="r"]');
    var atStart = x < 8, atEnd = x > max - 8;
    if (l) { l.style.opacity = atStart ? '.35' : '1'; l.style.pointerEvents = atStart ? 'none' : 'auto'; }
    if (r) { r.style.opacity = atEnd ? '.35' : '1'; r.style.pointerEvents = atEnd ? 'none' : 'auto'; }
    if (fl) fl.style.opacity = atStart ? '0' : '1';
    if (fr) fr.style.opacity = atEnd ? '0' : '1';
  }
  if (ugRail) {
    ugRail.addEventListener('scroll', function () {
      if (ugRail._t) return;
      ugRail._t = requestAnimationFrame(function () { ugRail._t = 0; ugSync(); });
    }, { passive: true });
    ugSync();
  }

  /* ══ REVEALS + COUNT-UPS ═══════════════════════════════════════════════ */
  if (!REDUCED && window.IntersectionObserver) {
    ROOT.setAttribute('data-mv6-anim', '');
    /* Each entry is [element to WATCH, elements to MARK, stagger ms]. They are
       split because an SVG <path> is not reliably observable — a stroke has no
       layout box of its own, which is why the formula's three converging lines
       and the timeline arc never drew. Watch the <svg>, mark the paths. */
    var pairs = [];
    $$('[data-mv6-rise]').forEach(function (el) { pairs.push([el, [el], 150]); });
    var fmSvg = $('[data-mv6-fm-svg]');
    if (fmSvg) pairs.push([fmSvg, $$('[data-mv6-fm-line]', fmSvg), 190]);
    var arcEl = $('[data-mv6-arc]');
    if (arcEl) pairs.push([arcEl.ownerSVGElement || arcEl, [arcEl].concat($$('[data-mv6-arc-fill]')), 0]);

    /* Marking, factored out of the observer callback so the R-134 fallback
       below fires the SAME sequence. Idempotent — .fired guards both writers. */
    function firePair(i) {
      var pair = pairs[i];
      if (!pair || pair.fired) return;
      pair.fired = true;
      io.unobserve(pair[0]);
      var targets = pair[1], step = pair[2], base = 0;
      /* A lone [data-mv6-rise] staggers against its SIBLINGS, so a row of
         cards arrives one after another rather than all at once. */
      if (targets.length === 1 && pair[0].hasAttribute('data-mv6-rise') && pair[0].parentNode) {
        var sibs = Array.prototype.slice.call(pair[0].parentNode.querySelectorAll('[data-mv6-rise]'));
        /* data-mv6-rise-step overrides the 150ms default for a row whose
           siblings must arrive in sequence rather than nearly together.
           The timeline uses 620ms so each column lands under the arc as it
           draws (dot, then the label, then the review) instead of the whole
           row appearing while the arc is still travelling. */
        var st = parseInt(pair[0].getAttribute('data-mv6-rise-step'), 10) || step;
        base = Math.min(Math.max(0, sibs.indexOf(pair[0])), 5) * st;
        step = 0;
      }
      targets.forEach(function (t, k) {
        setTimeout(function () { t.setAttribute('data-mv6-in', ''); }, base + k * step);
      });
    }

    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        /* Cold-layout guard (R-135): no reveal fires on an UNTRUSTED layout —
           see the trust block below. The deferred pair is caught by
           sweepReveals when trust arrives (position math over real rects). */
        if (!layoutTrusted && (window.scrollY || 0) < 40) return;
        for (var i = 0; i < pairs.length; i++) {
          if (pairs[i][0] === e.target) { firePair(i); break; }
        }
      });
      /* v6.35 — the trigger line was 6% up from the viewport bottom, so a block
         revealed the instant its top edge appeared: the animation ran in the
         last sliver of the screen and the user's report was that they "barely
         notice it". The line is now 24% up, which is roughly where a phone
         reader's eye actually is, so the rise plays in reading position. Raise
         this number to delay further; do not lower it past ~12% or the reveal
         goes back to happening off the bottom edge. */
    }, { threshold: 0.14, rootMargin: '0px 0px -24% 0px' });
    pairs.forEach(function (p) { io.observe(p[0]); });

    /* ⚠ R-135 · NO REVEAL FIRES ON AN UNTRUSTED LAYOUT (23 Aug 2026). First
       visit, cold cache: the document is SHORT until images size, so at t≈0
       every chapter sits inside the first viewport — the observer fires for
       ALL of them, the animations play unseen, and the user scrolls into
       already-filled sections (the "1-2-3 numerals pre-filled" report, and
       the same pre-filled frames in the 23 Aug Clarity sessions on live
       v6.47; a refresh "fixes" it because the warm cache sizes the layout
       instantly). Layout is TRUSTED once window load fires, or the document
       height holds still across two 450ms samples; a user who has actually
       scrolled (y ≥ 40) is also trusted — real rects at their position are
       the truth. Until trust: the observer defers (sweepReveals re-checks
       every deferred pair by position when trust arrives) and the bottom-of-
       page clamp below stays OFF — on the collapsed document yMax≈0 made
       that clamp fire EVERYTHING. */
    var layoutTrusted = document.readyState === 'complete';
    var lastDocH = -1;
    function trustLayout() {
      if (layoutTrusted) return;
      layoutTrusted = true;
      measureReveals();
    }
    if (!layoutTrusted) {
      window.addEventListener('load', trustLayout);
      var stableT = setInterval(function () {
        if (layoutTrusted) { clearInterval(stableT); return; }
        var hNow = document.documentElement.scrollHeight;
        if (hNow === lastDocH) { clearInterval(stableT); trustLayout(); }
        lastDocH = hNow;
      }, 450);
    }
    /* Trust is not immutable: below-fold LAZY images size after window load
       and grow the document — unfired tops measured before that growth are
       stale. Watch height for 8s post-init and re-measure on any ≥80px
       change (measureReveals only touches UNfired pairs, so this can never
       replay a reveal). */
    var growN = 0, growH = document.documentElement.scrollHeight;
    var growT = setInterval(function () {
      if (++growN > 18) { clearInterval(growT); return; }
      var hNow = document.documentElement.scrollHeight;
      if (Math.abs(hNow - growH) >= 80) { growH = hNow; if (layoutTrusted) measureReveals(); }
    }, 450);

    /* ⚠ R-134 · THE REVEAL RESOLVES WITHOUT THE OBSERVER (23 Aug 2026).
       A tablet Clarity session scrolled the whole timeline chapter with the
       heading and the SVG's static grid lines visible and EVERY gated element
       missing — data-mv6-anim set, no data-mv6-in ever written. The observer
       above was this module's only writer, so one late, throttled or dropped
       IO callback leaves a chapter invisible for the rest of the visit — the
       same "a state nothing will overwrite" shape as R-118/R-121. Belt with
       the rails' discipline: document-coordinate tops measured OFF the scroll
       path (R-89 — load, the 400/1400/2600ms settles, width resizes), pure
       arithmetic per scroll frame, and every pair fires the moment the page
       has scrolled past its reveal line whether or not the observer spoke.
       The line is the observer's own 76% line, so when both work the paint is
       identical; firePair's guard makes the race harmless. */
    var revTops = null;
    function measureReveals() {
      var y = window.scrollY || 0, any = false;
      revTops = [];
      for (var i = 0; i < pairs.length; i++) {
        if (pairs[i].fired) { revTops.push(Infinity); continue; }
        revTops.push(pairs[i][0].getBoundingClientRect().top + y);
        any = true;
      }
      if (!any) revTops = null;
      sweepReveals();
    }
    function sweepReveals() {
      if (!revTops) return;
      if (!layoutTrusted && (window.scrollY || 0) < 40) return;   /* R-135 */
      var vh = window.innerHeight || 800;
      var y = window.scrollY || 0;
      var line = y + vh * 0.76;
      /* At the very bottom of the document nothing below the 76% line can ever
         cross it — resolve the last viewport too (R-121: off-limits states are
         written, never assumed). TRUSTED layouts only (R-135): on the cold
         collapsed document yMax≈0 made this clamp fire every pair at load. */
      var yMax = Math.max(0, (document.documentElement.scrollHeight || 0) - vh);
      if (layoutTrusted && yMax > vh / 2 && y >= yMax - 2) line = y + vh + 1;
      var left = false;
      for (var i = 0; i < pairs.length; i++) {
        if (pairs[i].fired || revTops[i] === Infinity) continue;
        if (revTops[i] < line) { firePair(i); revTops[i] = Infinity; }
        else left = true;
      }
      if (!left) revTops = null;
    }
    var revTick = false;
    window.addEventListener('scroll', function () {
      if (revTick || !revTops) return;
      revTick = true;
      requestAnimationFrame(function () { revTick = false; sweepReveals(); });
    }, { passive: true });
    var lastRevW = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastRevW) return;   /* R-71: height-only resizes are iOS chrome noise */
      lastRevW = window.innerWidth;
      measureReveals();
    }, { passive: true });
    if (document.readyState !== 'complete') window.addEventListener('load', function () { measureReveals(); });
    setTimeout(measureReveals, 400);
    setTimeout(measureReveals, 1400);
    setTimeout(measureReveals, 2600);
  }

  /* ══ SCROLL-PROGRESS RAILS ═════════════════════════════════════════
   * How-to-use's 01/02/03 numerals FILL as the reader scrolls, and the
   * why-three ingredient dots fade and lift in sequence. Both are progress
   * driven and reversible on the way back up — not an on/off reveal, which is
   * why the how-to section read as having no animation at all. Ported from the
   * design's _scrub(); the constants are its constants. */
  /* ⚠ R-89 · NO LAYOUT READS ON THE SCROLL PATH (round 30, 15 Aug 2026).
     Each rail's box was read with getBoundingClientRect() inside the scroll
     rAF — a forced synchronous layout per frame, per rail, alongside the same
     mistake in mv6-home.js and mv6-calm-rest.js. Three modules forcing layout
     while the header wrote a transform into a fixed element is R-71's defect
     ("micro freezes, text shimmer") relocated to the scroll path, and it is
     what the user has been reporting as "the whole page is shaking / flashy
     while I scroll". The rails are measured in DOCUMENT coordinates off the
     scroll path; a frame is arithmetic on scrollY plus style writes. */
  var RAILS = [
    ['[data-mv6-ht-rail]', '[data-mv6-step-n]', function (el, f) {
      var pc = (f * 100).toFixed(1) + '%';
      el.style.backgroundImage = 'linear-gradient(to top,#4A3A6E ' + pc + ',#FFFFFF ' + pc + ')';
      el.style.color = f > 0.55 ? '#FFFFFF' : '#4A3A6E';
      el.style.borderColor = f >= 1 ? '#4A3A6E' : '#C9BBDF';
    }],
    ['[data-mv6-w3-rail]', '[data-mv6-w3-n]', function (el, f) {
      el.style.opacity = (0.28 + 0.72 * f).toFixed(3);
      el.style.transform = 'translateY(' + ((1 - f) * 9).toFixed(1) + 'px)';
    }]
  ].map(function (r) { return { sel: r[0], nSel: r[1], apply: r[2], el: null, nodes: [], top: 0, h: 0, lastP: null }; });

  function measureRails() {
    RAILS.forEach(function (r) {
      r.el = $(r.sel);
      if (!r.el) return;
      var b = r.el.getBoundingClientRect();
      r.top = b.top + (window.scrollY || 0);
      r.h = b.height;
      r.nodes = $$(r.nSel, r.el);
      r.lastP = null;   /* geometry moved — the last paint is no longer trusted (R-121) */
    });
  }

  function scrub(force) {
    var vh = window.innerHeight || 800;
    var y = window.scrollY || 0;
    RAILS.forEach(function (r) {
      if (!r.el) return;
      var top = r.top - y;
      /* ⚠ R-121 · AN OFF-SCREEN RAIL IS RESOLVED, NEVER SKIPPED (17 Aug 2026).
         This used to be `if (top + r.h < -60 || top > vh + 60) return;` — a
         cheap early-out that LEAVES WHATEVER WAS LAST PAINTED ON THE NODES.
         That is what the user reported as "sometimes on mobile the 01/02/03 in
         How to use are already filled in": on a phone the rail is measured
         before the images above it have laid out, so `r.top` comes back far too
         small, the rail looks like it is already on screen, and the first scrub
         paints it FULL. The 400/1400/2600ms re-measures then fix `r.top`
         correctly — at which point the rail is genuinely below the fold, the
         early-out fires, and the wrong paint is preserved for the rest of the
         session. The numerals were never "pre-filled"; they were correctly
         filled against a wrong measurement and then never corrected.

         Same shape as R-118's count-up defect: the element is readable in a
         state nothing will overwrite. The fix is the same one — every state is
         WRITTEN, none is assumed. Below the fold resolves to 0, above it to 1,
         and `lastP` keeps the scroll path free of redundant style writes so
         resolving instead of skipping costs nothing per frame (R-89). */
      var p;
      if (force) p = 1;
      else if (top > vh + 60) p = 0;
      else if (top + r.h < -60) p = 1;
      else {
        /* v6.35 — the scrub window. It used to start filling when the rail's top
           was 8% below the viewport bottom and complete over 55% of a screen, so
           on a fast scroll the fill was finished before the block was readable.
           It now starts at 78% down (the rail is properly on screen first) and
           runs over 70% of a screen, so the fill tracks the read rather than
           racing it. First number = where filling starts, second = how much
           scrolling it takes to complete. */
        p = Math.max(0, Math.min(1, (vh * 0.78 - top) / (vh * 0.70)));
      }
      if (r.lastP === p) return;
      r.lastP = p;
      r.nodes.forEach(function (el, j) {
        r.apply(el, Math.max(0, Math.min(1, p * 3 - j)));
      });
    });
  }
  measureRails();
  /* SEED (R-121). Paint every node at 0 before any scroll, measurement or
     observer runs, so the resting state is a state this module WROTE. Liquid
     renders the numeral on a pale chip with no fill; if a later measurement is
     wrong the worst case is now an unfilled numeral, never a filled one. */
  if (!REDUCED) RAILS.forEach(function (r) {
    r.nodes.forEach(function (el) { r.apply(el, 0); });
  });
  if (REDUCED) scrub(true);
  else {
    var scrubbing = false;
    window.addEventListener('scroll', function () {
      if (scrubbing) return;
      scrubbing = true;
      requestAnimationFrame(function () { scrubbing = false; scrub(false); });
    }, { passive: true });
    var lastRailW = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastRailW) return;   /* R-71 */
      lastRailW = window.innerWidth;
      measureRails(); scrub(false);
    }, { passive: true });
    if (document.readyState !== 'complete') window.addEventListener('load', function () { measureRails(); scrub(false); });
    setTimeout(function () { measureRails(); scrub(false); }, 400);
    setTimeout(function () { measureRails(); scrub(false); }, 1400);
    setTimeout(function () { measureRails(); scrub(false); }, 2600);
    scrub(false);
  }

  if (window.IntersectionObserver) {
    var cio = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        cio.unobserve(e.target);
        countUp(e.target);
      });
      /* v6.36 — REVERTED to -5%, and here is the real defect it exposed.
         The count value is rendered by Liquid at its FINAL number; this script
         only overwrites it when the observer fires. At -5% the observer fires
         the instant the element enters, so nobody ever saw the static value and
         the bug was invisible. v6.35 moved the trigger to -20% to make the
         animation land in reading position — and the user immediately reported
         "I see 90, then it animates to 90 again", because for those extra
         ~15% of a screen the final number was sitting there in plain sight.

         So the trigger timing was never the thing to change. The permanent fix
         is to SEED each [data-mv6-count] to its start value as soon as this
         script runs — before any observer — so the element is never readable at
         its final value. That needs countUp's formatting (thousands separators,
         decimals, the "+" suffix) factored out of the animation loop, which is a
         bigger change than tonight's ship deserves. Until then the trigger stays
         early, where the defect cannot be seen.
         ⚠ Do not "improve" this number again without doing the seeding first. */
    }, { threshold: 0.25, rootMargin: '0px 0px -5% 0px' });
    $$('[data-mv6-count]').forEach(function (el) { cio.observe(el); });
  }
  function countUp(el) {
    if (REDUCED) return;
    var to = parseFloat(el.getAttribute('data-mv6-count')) || 0;
    var from = parseFloat(el.getAttribute('data-mv6-count-from')) || 0;
    var suffix = el.getAttribute('data-mv6-count-suffix') || '';
    var t0 = performance.now();
    (function tick() {
      var raw = Math.min(1, (performance.now() - t0) / 1400);
      var p = 1 - Math.pow(1 - raw, 3);
      el.textContent = String(Math.round(from + (to - from) * p)) + suffix;
      if (raw < 1) requestAnimationFrame(tick);
    })();
  }

  /* Rows the MARKUP renders open (buy-box row 1, the first formula card, the
     first benefit row, FAQ 1). Sync the parts markup cannot carry — chevron
     rotation, the +/− glyph, the white open-card wrapper, the benefit photo —
     so nothing has to flip after the first paint. */
  $$('[data-mv6-acc][aria-expanded="true"]').forEach(function (b) {
    accSet(b.getAttribute('data-mv6-acc'), true, b);
  });
  var bootBen = $('[data-mv6-ben][aria-expanded="true"]');
  if (bootBen) benPhoto(bootBen.getAttribute('data-mv6-ben'));

  /* ══ ONE DELEGATED CLICK HANDLER ═══════════════════════════════════════ */
  /* ══ EMAIL IS NEVER A LINK ═════════════════════════════════════════════
     R-59. The addresses are split across two spans in the markup so there is no
     email-shaped text run for a linkifier to match. This is the layer for the
     ones that get created anyway, after we paint — iOS Safari's data detectors
     and app scripts both do it, which is why the fix held on the preview and
     not in production.

     Two parts, because they answer different failure modes:
       · the capture-phase click kill GUARANTEES the tap does nothing, even for
         an anchor created between two sweeps;
       · unwrap() puts the plain text back, so it also stops LOOKING like a link.
     The sweep is debounced off any click in the root, which is what catches an
     address inside an accordion panel that was `hidden` until it was opened. */
  function unwrapMailto() {
    $$('a[href^="mailto:"]').forEach(function (a) {
      if (!a.parentNode) return;
      var s = document.createElement('span');
      s.setAttribute('data-mv6-email', '');
      s.textContent = a.textContent;
      a.parentNode.replaceChild(s, a);
    });
  }
  var mailtoT = 0;
  ROOT.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="mailto:"]') : null;
    if (a) { e.preventDefault(); e.stopPropagation(); }
    clearTimeout(mailtoT);
    mailtoT = setTimeout(unwrapMailto, 60);
  }, true);
  unwrapMailto();
  window.addEventListener('load', function () { setTimeout(unwrapMailto, 300); }, { once: true });
  setTimeout(unwrapMailto, 2500);

  /* ══ ONE DELEGATED CLICK HANDLER ═══════════════════════════════════════ */
  ROOT.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target : null;
    if (!t) return;

    var el = t.closest('[data-mv6-gal-dot],[data-mv6-gal-thumb],[data-mv6-gal-step],[data-mv6-lb-close],[data-mv6-lb-step],[data-mv6-gv-play],[data-mv6-facts-open],[data-mv6-facts-close],[data-mv6-facts-scrim],[data-mv6-fm-jump],[data-mv6-acc],[data-mv6-topic],[data-mv6-vid-step],[data-mv6-vid-dot],[data-mv6-vid-play],[data-mv6-ug-arrow],[data-mv6-ug-dot],[data-mv6-ft],[data-mv6-rev-jump]');
    if (!el || !ROOT.contains(el)) return;

    if (el.hasAttribute('data-mv6-gal-dot')) { setImg(parseInt(el.getAttribute('data-mv6-gal-dot'), 10)); return; }
    if (el.hasAttribute('data-mv6-gal-thumb')) { setImg(parseInt(el.getAttribute('data-mv6-gal-thumb'), 10)); return; }
    if (el.hasAttribute('data-mv6-gal-step')) { setImg(img + parseInt(el.getAttribute('data-mv6-gal-step'), 10)); return; }
    if (el.hasAttribute('data-mv6-lb-close')) { lbSet(false); return; }
    if (el.hasAttribute('data-mv6-lb-step')) { lbNav(parseInt(el.getAttribute('data-mv6-lb-step'), 10)); return; }
    if (el.hasAttribute('data-mv6-gv-play')) { gvPlay(); return; }

    if (el.hasAttribute('data-mv6-facts-open')) { setFacts(true); return; }
    if (el.hasAttribute('data-mv6-facts-close') || el.hasAttribute('data-mv6-facts-scrim')) { setFacts(false); return; }

    /* Pathway icons (23 Aug 2026, Clarity dead-clicks): open the matching
       ingredient card and travel to it. Never scrollIntoView — rect math, the
       header-jump pattern; 84px clears the pinned nav plus breathing room. */
    if (el.hasAttribute('data-mv6-fm-jump')) {
      var jk = el.getAttribute('data-mv6-fm-jump');
      var jb = $('[data-mv6-acc="' + jk + '"]');
      accSet(jk, true, jb);
      var jc = jb && jb.closest('[data-mv6-ing]');
      if (jc) {
        var jy = jc.getBoundingClientRect().top + (window.scrollY || 0) - 84;
        window.scrollTo({ top: Math.max(0, jy), behavior: REDUCED ? 'auto' : 'smooth' });
      }
      return;
    }

    if (el.hasAttribute('data-mv6-acc')) {
      var key = el.getAttribute('data-mv6-acc');
      var open = el.getAttribute('aria-expanded') !== 'true';
      /* Benefits is single-open; everything else toggles independently. */
      if (el.hasAttribute('data-mv6-ben')) {
        $$('[data-mv6-ben]').forEach(function (b) {
          if (b !== el) accSet(b.getAttribute('data-mv6-acc'), false, b);
        });
        benPhoto(open ? el.getAttribute('data-mv6-ben') : null);
      }
      accSet(key, open, el);
      return;
    }

    if (el.hasAttribute('data-mv6-topic')) { setTopic(parseInt(el.getAttribute('data-mv6-topic'), 10)); return; }

    if (el.hasAttribute('data-mv6-vid-step')) { vidStep(parseInt(el.getAttribute('data-mv6-vid-step'), 10)); return; }
    if (el.hasAttribute('data-mv6-vid-dot')) { vidStopAll(); vidIdx = parseInt(el.getAttribute('data-mv6-vid-dot'), 10); vidPaint(false); return; }
    if (el.hasAttribute('data-mv6-vid-play')) { vidPlay(el.closest('[data-mv6-vid-slot]')); return; }

    if (el.hasAttribute('data-mv6-ug-arrow')) {
      /* One card per click: name the index, never scroll a distance. */
      ugGo(ugIndex() + parseInt(el.getAttribute('data-mv6-ug-arrow'), 10));
      return;
    }
    if (el.hasAttribute('data-mv6-ug-dot')) { ugGo(parseInt(el.getAttribute('data-mv6-ug-dot'), 10)); return; }

    /* Footer columns collapse on mobile only. */
    if (el.hasAttribute('data-mv6-ft')) {
      if (!isMobile()) return;
      var i = el.getAttribute('data-mv6-ft');
      var panel = $('[data-mv6-ft-panel="' + i + '"]');
      if (!panel) return;
      var on = el.getAttribute('aria-expanded') !== 'true';
      panel.style.display = on ? 'flex' : 'none';
      el.setAttribute('aria-expanded', on ? 'true' : 'false');
      var chev = el.querySelector('[data-mv6-acc-chev]');
      if (chev) chev.style.transform = on ? 'rotate(180deg)' : 'rotate(0deg)';
      return;
    }

    if (el.hasAttribute('data-mv6-rev-jump')) {
      e.preventDefault();
      scrollToEl($('[data-mv6-rev-anchor]'), 16);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && factsOpen) setFacts(false);
  });

  /* R-128 · bfcache: a back-forward restore brings the sheet back exactly as it was
     left — Clarity sessions that "start" with the sheet open are these restores.
     Close it and release its body pin. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && factsOpen) setFacts(false);
  });

  window.addEventListener('resize', function () {
    vidPaint(true);
    ugSync();
    if (!isMobile()) fxLock(false);
  }, { passive: true });

  /* ══ REVIEWS WALL — Judge.me's own public widget endpoint ══════════════
   * CORS-open, no token, the same data their widget renders from. Everything
   * below is our markup, so sort / filter / paging move nothing on screen
   * except the list. */
  var rv = { all: [], hist: [], ai: null, sort: 'recent', star: 0, page: 1 };
  var PER = 5;

  function rvUrl(page) {
    return 'https://cdn.judge.me/reviews/reviews_for_widget?url=' + encodeURIComponent(SHOP) +
      '&shop_domain=' + encodeURIComponent(SHOP) +
      '&platform=shopify&product_id=' + encodeURIComponent(PRODUCT_ID) +
      '&per_page=30&page=' + page +
      '&ts=' + Math.floor(Date.now() / 600000);
  }

  /* The AI summary is machine-written text that WE choose to print, so the
     page's named-condition rule applies to it: a disease word turns a
     structure/function claim into a disease claim. Customer reviews are NOT
     passed through this — those are their own words. */
  function rvSafe(t) {
    return String(t || '')
      .replace(/reduce anxiety/gi, 'take the edge off a demanding day')
      .replace(/reduces anxiety/gi, 'takes the edge off a demanding day')
      .replace(/reducing anxiety/gi, 'taking the edge off a demanding day')
      .replace(/\banxiety\b/gi, 'stress')
      .replace(/\binsomnia\b/gi, 'restless nights')
      .replace(/\bdepression\b/gi, 'low mood');
  }
  function rvText(html) {
    var d = document.createElement('div');
    d.innerHTML = String(html || '');
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  }
  /* Dates are pinned to UTC deliberately: most reviews are stamped 16:00Z, so
     parsing to local time renders the same review a day apart either side of
     UTC — two surfaces stating different facts about one review. */
  function rvDate(s) {
    var m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    try {
      return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    } catch (e) { return m[2] + '/' + m[3] + '/' + m[1]; }
  }
  /* R-120 · stars are SVG, never a text character. See snippets/mv6-stars.liquid
     for why (U+2605 rendered as five orange CJK glyphs on the live site). These
     go through innerHTML with the review markup, so they are the same path.
     The containing span must be inline-flex with a gap — letter-spacing does
     nothing to an SVG run. */
  var STAR_PATH = 'M10 .8L12.59 6.64 18.94 7.3 14.18 11.56 15.53 17.8 10 14.6 4.47 17.8 5.82 11.56 1.06 7.3 7.41 6.64Z';
  function stars(n) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += i <= n
        ? '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false" style="display:block;flex:none;width:.92em;height:.92em"><path d="' + STAR_PATH + '"></path></svg>'
        : '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false" style="display:block;flex:none;width:.92em;height:.92em"><path d="' + STAR_PATH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"></path></svg>';
    }
    return out;
  }
  function rvVoted() {
    try { return JSON.parse(localStorage.getItem('evl-rv-thumbs') || '{}'); } catch (e) { return {}; }
  }
  function rvList() {
    var a = rv.all.slice();
    if (rv.star) a = a.filter(function (r) { return r.rating === rv.star; });
    var t = function (x) { return Date.parse(x.created_at) || 0; };
    if (rv.sort === 'high') a.sort(function (x, y) { return y.rating - x.rating || t(y) - t(x); });
    else if (rv.sort === 'low') a.sort(function (x, y) { return x.rating - y.rating || t(y) - t(x); });
    else if (rv.sort === 'helpful') a.sort(function (x, y) { return (y.thumb_up || 0) - (x.thumb_up || 0) || t(y) - t(x); });
    else a.sort(function (x, y) { return t(y) - t(x); });
    return a;
  }
  function rvJump() {
    var el = $('[data-mv6-rv-top]');
    if (!el) return;
    var nav = $('[data-mv6-nav]');
    var pad = (nav ? nav.getBoundingClientRect().height : 84) + 10;
    var to = el.getBoundingClientRect().top + (window.scrollY || 0) - pad;
    if (Math.abs((window.scrollY || 0) - to) < 4) return;
    window.scrollTo({ top: Math.max(0, to), behavior: REDUCED ? 'auto' : 'smooth' });
  }

  function jdgmPaint(r, n) {
    if (!LIVE_STATS || !r || !n) return;
    var rs = r.toFixed(1), ns = n.toLocaleString('en-US');
    $$('[data-jdgm-txt]').forEach(function (el) {
      var s = el.getAttribute('data-jdgm-txt').replace('{r}', rs).replace('{n}', ns);
      if (el.textContent !== s) el.textContent = s;
    });
    var w = Math.max(0, Math.min(100, (r / 5) * 100)).toFixed(1) + '%';
    $$('[data-jdgm-fill]').forEach(function (el) { if (el.style.width !== w) el.style.width = w; });
  }

  function rvRender() {
    var list = rvList(), total = list.length;
    var pages = Math.max(1, Math.ceil(total / PER));
    if (rv.page > pages) rv.page = pages;

    /* histogram — each row is a filter */
    var hist = $('[data-mv6-rv-hist]');
    if (hist) {
      show(hist, rv.hist.length > 0);
      hist.style.display = rv.hist.length ? 'grid' : 'none';
      hist.innerHTML = rv.hist.map(function (row) {
        var on = rv.star === row.rating;
        return '<button type="button" data-mv6-rv-hist-row="' + row.rating + '" aria-label="Show only ' + row.rating + '-star reviews (' + row.frequency + ')"' +
          ' style="display:flex; align-items:center; gap:10px; width:100%; padding:3px 7px; border:none; border-radius:8px; background:' + (on ? '#EDE6F6' : 'transparent') + '; cursor:pointer; font-family:inherit !important; text-align:left">' +
          '<span aria-hidden="true" translate="no" style="flex:none; display:inline-flex; align-items:center; gap:.09em; color:#F5B31E; font-size:13px; line-height:1">' + stars(row.rating) + '</span>' +
          '<span style="flex:1 1 auto; min-width:0; height:9px; border-radius:999px; background:#F0EAF7; overflow:hidden"><span style="display:block; height:100%; width:' + (row.percentage || 0) + '%; border-radius:999px; background:#4A3A6E; transition:width .5s cubic-bezier(.22,1,.36,1)"></span></span>' +
          '<span style="flex:none; min-width:26px; font-size:12.5px; font-weight:600; color:#6A6378; text-align:right">' + row.frequency + '</span></button>';
      }).join('');
    }
    var note = $('[data-mv6-rv-starnote]');
    if (note) {
      show(note, !!rv.star);
      note.style.display = rv.star ? 'flex' : 'none';
      note.textContent = rv.star ? rv.star + '-star reviews only' : '';
    }

    /* AI summary + highlights */
    var ai = $('[data-mv6-rv-ai]');
    if (ai) {
      var hasAi = !!(rv.ai && rv.ai.text);
      show(ai, hasAi);
      ai.style.display = hasAi ? 'flex' : 'none';
      if (hasAi) $('[data-mv6-rv-ai-text]').textContent = rvSafe(rv.ai.text);
      var hls = (rv.ai && rv.ai.highlights) || [];
      var hlWrap = $('[data-mv6-rv-hl-wrap]');
      show(hlWrap, hls.length > 0);
      if (hls.length) {
        $('[data-mv6-rv-hl]').innerHTML = hls.map(function (x) {
          return '<button type="button" data-mv6-rv-hl-item="' + esc(x.review_uuid) + '" style="display:flex; align-items:flex-start; gap:9px; width:100%; padding:8px 10px; margin-left:-10px; border:none; background:none; cursor:pointer; font-family:var(--font-display) !important; font-size:var(--rv-hl-fs) !important; line-height:1.42 !important; color:#2A2435; text-align:left; border-radius:12px; transition:background .2s ease">' +
            '<span style="flex:1 1 auto; min-width:0; text-wrap:pretty">“' + esc(rvSafe(x.highlight)) + '”</span>' +
            '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="flex:none; margin-top:4px"><path d="M9 6l6 6-6 6" stroke="#8B7BA4" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>';
        }).join('');
      }
    }

    /* sort row */
    var showing = $('[data-mv6-rv-showing]');
    if (showing) {
      showing.textContent = total
        ? 'Showing ' + ((rv.page - 1) * PER + 1) + '–' + Math.min(total, rv.page * PER) + ' of ' + total.toLocaleString('en-US')
        : '';
    }
    $$('[data-mv6-rv-sort]').forEach(function (b) {
      var on = b.getAttribute('data-mv6-rv-sort') === rv.sort;
      b.style.background = on ? '#4A3A6E' : '#FFFFFF';
      b.style.color = on ? '#FFFFFF' : '#4A3A6E';
      b.style.borderColor = on ? '#4A3A6E' : '#D8CDE9';
    });
    var clear = $('[data-mv6-rv-clear]');
    if (clear) {
      var dirty = rv.star || rv.sort !== 'recent';
      show(clear, dirty);
      clear.style.display = dirty ? 'inline-flex' : 'none';
    }

    /* the reviews */
    var voted = rvVoted();
    var listEl = $('[data-mv6-rv-list]');
    if (listEl) {
      listEl.innerHTML = list.slice((rv.page - 1) * PER, rv.page * PER).map(function (r, i) {
        var v = voted[r.uuid];
        return '<div data-mv6-rv-uuid="' + esc(r.uuid) + '" style="background:transparent; border:none;' + (i > 0 ? ' border-top:1px solid #EFEAF6;' : '') + ' padding:var(--rv-card-pad); transition:box-shadow .4s ease">' +
          '<div style="display:flex; align-items:center; justify-content:space-between; gap:10px">' +
            '<span aria-hidden="true" translate="no" style="display:inline-flex; align-items:center; gap:.15em; color:#F5B31E; font-size:15px; line-height:1">' + stars(r.rating) + '</span>' +
            '<span style="font-size:12px; color:#8B7BA4; white-space:nowrap">' + esc(rvDate(r.created_at)) + '</span>' +
          '</div>' +
          '<div style="display:flex; align-items:center; gap:7px; margin-top:9px; flex-wrap:wrap">' +
            '<span style="font-size:13.5px; font-weight:700; color:#2A2435">' + esc(r.reviewer_name || 'Verified customer') + '</span>' +
            (r.verified_buyer ? '<span style="display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:700; color:#6E8B4C"><span aria-hidden="true" style="width:13px; height:13px; border-radius:999px; background:#95DFC7; display:inline-flex; align-items:center; justify-content:center"><svg width="8" height="8" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7" stroke="#33604A" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>Verified</span>' : '') +
          '</div>' +
          (r.title ? '<div style="font-family:var(--font-display) !important; font-weight:600; font-size:var(--rv-title-fs); line-height:1.25; color:#2A2435; margin-top:8px">' + esc(rvText(String(r.title).replace(/</g, '&lt;'))) + '</div>' : '') +
          '<p style="font-size:14px; line-height:1.6; color:#3A3344; margin:7px 0 0; max-width:var(--rv-body-maxw); text-wrap:pretty">' + esc(rvText(r.body_html || r.body)) + '</p>' +
          '<div style="display:flex; align-items:center; gap:14px; margin-top:12px">' +
            '<button type="button" data-mv6-rv-vote="up" data-uuid="' + esc(r.uuid) + '" aria-label="Helpful" style="display:inline-flex; align-items:center; gap:6px; padding:4px 9px; border:1px solid ' + (v === 'up' ? '#4A3A6E' : '#E7DEF4') + '; border-radius:999px; background:' + (v === 'up' ? '#EDE6F6' : '#FFFFFF') + '; cursor:' + (v ? 'default' : 'pointer') + '; font-family:inherit !important; font-size:12px !important; font-weight:600 !important; color:#4A3A6E"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 21V10l5-7 1.2.8a2 2 0 011.7 2.2L13 9h5.2a2 2 0 011.95 2.45l-1.6 7A2 2 0 0116.6 20H7z" stroke="#4A3A6E" stroke-width="1.7" stroke-linejoin="round"></path></svg>' + (r.thumb_up || 0) + '</button>' +
            '<button type="button" data-mv6-rv-vote="down" data-uuid="' + esc(r.uuid) + '" aria-label="Not helpful" style="display:inline-flex; align-items:center; gap:6px; padding:4px 9px; border:1px solid ' + (v === 'down' ? '#8B7BA4' : '#E7DEF4') + '; border-radius:999px; background:' + (v === 'down' ? '#EDE6F6' : '#FFFFFF') + '; cursor:' + (v ? 'default' : 'pointer') + '; font-family:inherit !important; font-size:12px !important; font-weight:600 !important; color:#8B7BA4"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M17 3v11l-5 7-1.2-.8a2 2 0 01-.7-2.2L11 15H5.8a2 2 0 01-1.95-2.45l1.6-7A2 2 0 017.4 4H17z" stroke="#8B7BA4" stroke-width="1.7" stroke-linejoin="round"></path></svg>' + (r.thumb_down || 0) + '</button>' +
          '</div></div>';
      }).join('');
    }

    /* pagination — ours, so nothing scrolls anywhere it shouldn't */
    var pag = $('[data-mv6-rv-pag]');
    if (pag) {
      var many = total > PER;
      show(pag, many);
      pag.style.display = many ? 'flex' : 'none';
      if (many) {
        var a = Math.max(1, rv.page - 2), b = Math.min(pages, a + 4);
        a = Math.max(1, b - 4);
        var out = '<button type="button" data-mv6-rv-nav="-1" aria-label="Previous page" style="display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px; border:1px solid #D8CDE9; border-radius:999px; background:#FFFFFF; cursor:pointer; opacity:' + (rv.page <= 1 ? '.35' : '1') + '; pointer-events:' + (rv.page <= 1 ? 'none' : 'auto') + '"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 5l-7 7 7 7" stroke="#4A3A6E" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>';
        for (var p = a; p <= b; p++) {
          var on = p === rv.page;
          out += '<button type="button" data-mv6-rv-page="' + p + '" aria-label="Page ' + p + '" style="min-width:34px; height:34px; padding:0 10px; border:1px solid ' + (on ? '#4A3A6E' : '#E7DEF4') + '; border-radius:999px; background:' + (on ? '#4A3A6E' : '#FFFFFF') + '; color:' + (on ? '#FFFFFF' : '#4A3A6E') + '; font-family:inherit !important; font-size:13px !important; font-weight:700 !important; cursor:pointer">' + p + '</button>';
        }
        out += '<button type="button" data-mv6-rv-nav="1" aria-label="Next page" style="display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px; border:1px solid #D8CDE9; border-radius:999px; background:#FFFFFF; cursor:pointer; opacity:' + (rv.page >= pages ? '.35' : '1') + '; pointer-events:' + (rv.page >= pages ? 'none' : 'auto') + '"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5l7 7-7 7" stroke="#4A3A6E" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>';
        pag.innerHTML = out;
      }
    }
    var load = $('[data-mv6-rv-load]');
    if (load) load.style.display = 'none';
  }

  function rvLoad() {
    var grab = function (p) { return fetch(rvUrl(p)).then(function (r) { return r.json(); }); };
    grab(1).then(function (j) {
      var n = parseInt(j.number_of_reviews, 10) || 0;
      var avg = parseFloat(j.average_rating) || 0;
      if (n > 0 && avg > 0) jdgmPaint(avg, n);
      rv.all = j.reviews || [];
      rv.hist = j.histogram || [];
      rv.ai = (j.ai_summary_translations && j.ai_summary_translations.en) || null;
      rvRender();
      var totalPages = (j.pagination && j.pagination.total_pages) || 1;
      var rest = [];
      for (var p = 2; p <= Math.min(totalPages, 6); p++) rest.push(grab(p).catch(function () { return null; }));
      if (!rest.length) return;
      Promise.all(rest).then(function (js) {
        var more = js.filter(Boolean).reduce(function (a, x) { return a.concat(x.reviews || []); }, []);
        rv.all = rv.all.concat(more);
        rvRender();
      }).catch(function () {});
    }).catch(function () {
      var load = $('[data-mv6-rv-load]');
      if (load) load.style.display = 'none';
      var err = $('[data-mv6-rv-err]');
      if (err) { err.removeAttribute('hidden'); err.style.display = 'flex'; }
    });
  }

  ROOT.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('[data-mv6-rv-sort],[data-mv6-rv-page],[data-mv6-rv-nav],[data-mv6-rv-hist-row],[data-mv6-rv-clear],[data-mv6-rv-hl-item],[data-mv6-rv-vote]') : null;
    if (!el || !ROOT.contains(el)) return;

    if (el.hasAttribute('data-mv6-rv-sort')) { rv.sort = el.getAttribute('data-mv6-rv-sort'); rv.page = 1; rvRender(); rvJump(); return; }
    if (el.hasAttribute('data-mv6-rv-page')) { rv.page = parseInt(el.getAttribute('data-mv6-rv-page'), 10); rvRender(); rvJump(); return; }
    if (el.hasAttribute('data-mv6-rv-nav')) {
      rv.page = Math.max(1, rv.page + parseInt(el.getAttribute('data-mv6-rv-nav'), 10));
      rvRender(); rvJump(); return;
    }
    if (el.hasAttribute('data-mv6-rv-hist-row')) {
      var r = parseInt(el.getAttribute('data-mv6-rv-hist-row'), 10);
      rv.star = rv.star === r ? 0 : r;
      rv.page = 1; rvRender(); rvJump(); return;
    }
    if (el.hasAttribute('data-mv6-rv-clear')) { rv.star = 0; rv.sort = 'recent'; rv.page = 1; rvRender(); rvJump(); return; }

    /* A highlight names a review by uuid: clear any filter, work out which of
       OUR pages holds it, switch, then land it under the header and flash it. */
    if (el.hasAttribute('data-mv6-rv-hl-item')) {
      var uuid = el.getAttribute('data-mv6-rv-hl-item');
      rv.star = 0; rv.sort = 'recent';
      var list = rvList(), idx = -1;
      for (var k = 0; k < list.length; k++) if (list[k].uuid === uuid) { idx = k; break; }
      if (idx < 0) return;
      rv.page = Math.floor(idx / PER) + 1;
      rvRender();
      requestAnimationFrame(function () {
        var card = $('[data-mv6-rv-uuid="' + uuid + '"]');
        if (!card) return;
        scrollToEl(card, 14);
        card.style.boxShadow = '0 0 0 2px #4A3A6E';
        setTimeout(function () { card.style.boxShadow = ''; }, 1500);
      });
      return;
    }

    /* Judge.me's vote endpoint is public. Optimistic count, one vote per
       review per browser, and a failed POST is silent. */
    if (el.hasAttribute('data-mv6-rv-vote')) {
      var uid = el.getAttribute('data-uuid');
      var voted = rvVoted();
      if (voted[uid]) return;
      var type = el.getAttribute('data-mv6-rv-vote');
      voted[uid] = type;
      try { localStorage.setItem('evl-rv-thumbs', JSON.stringify(voted)); } catch (err) {}
      rv.all = rv.all.map(function (r) {
        if (r.uuid !== uid) return r;
        var c = Object.assign({}, r);
        if (type === 'up') c.thumb_up = (c.thumb_up || 0) + 1; else c.thumb_down = (c.thumb_down || 0) + 1;
        return c;
      });
      rvRender();
      try {
        fetch('https://judge.me/thumbs', {
          method: 'POST', mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'review_uuid=' + encodeURIComponent(uid) + '&thumb_type=' + type
        }).catch(function () {});
      } catch (err) {}
    }
  });

  /* Page 1 is fetched immediately, not lazily: the star rows above the fold
     read their numbers from it. */
  if (WANT_REVIEWS || LIVE_STATS) {
    if (SHOP && PRODUCT_ID) rvLoad();
    else console.warn('[mv6-content] no shop/product id — reviews not loaded');
  }
})();
